import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import {
  Fingerprint, Users, ListOrdered,
  BarChart3, Terminal, ScrollText, FileCheck2, Settings2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { DeviceList } from '../components/DeviceList';
import { EmployeeEnrollTable } from '../components/EmployeeEnrollTable';
import { UnmappedPinsPanel } from '../components/UnmappedPinsPanel';
import { BiometricFullSettingsPanel } from '../components/BiometricFullSettings';
import { PunchLogTable } from '../components/PunchLogTable';
import { CommandQueuePanel } from '../components/CommandQueuePanel';
import { DeviceOpLogPanel } from '../components/DeviceOpLogPanel';
import { BiometricReports } from '../components/BiometricReports';
import { RegularisationPanel } from '../components/RegularisationPanel';
import { useBiometricLiveUpdates } from '../hooks/useBiometricAdmin';

const TABS = [
  { id: 'devices', label: 'Devices', icon: Fingerprint },
  { id: 'employees', label: 'Employees', icon: Users },
  { id: 'punches', label: 'Punches', icon: ListOrdered },
  { id: 'regularisations', label: 'Regularisations', icon: FileCheck2 },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
  { id: 'commands', label: 'Commands', icon: Terminal },
  { id: 'logs', label: 'Logs', icon: ScrollText },
  { id: 'settings', label: 'Settings', icon: Settings2 },
] as const;

type TabId = (typeof TABS)[number]['id'];

// ── Attendance Settings Panel ─────────────────────────────────────────────────
// Read/write to the settings collection only — never touches punch/enroll data.

interface AttendanceSettings {
  office_start_time: string;
  office_end_time: string;
  grace_in_minutes: number;
  half_day_after_minutes: number;
  absent_after_minutes: number;
  min_hours_full_day: number;
  min_hours_half_day: number;
  break_auto_deduct: boolean;
  break_minutes: number;
  checkout_guard_minutes: number;
  auto_approve_devices: boolean;
}

export function AttendanceSettingsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['biometric-settings'],
    queryFn: async () => (await api.get<{ settings: AttendanceSettings }>('/api/biometric/v2/settings')).data.settings,
  });

  const [draft, setDraft] = useState<Partial<AttendanceSettings>>({});
  const values = { ...data, ...draft } as AttendanceSettings;

  const save = useMutation({
    mutationFn: async () => (await api.patch('/api/biometric/v2/settings', draft)).data,
    onSuccess: () => {
      toast.success('Attendance settings saved.');
      setDraft({});
      qc.invalidateQueries({ queryKey: ['biometric-settings'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const set = <K extends keyof AttendanceSettings>(k: K, v: AttendanceSettings[K]) =>
    setDraft(prev => ({ ...prev, [k]: v }));

  if (isLoading) return <div className="p-8 text-center text-sm text-on-surface-variant">Loading settings…</div>;

  const dirty = Object.keys(draft).length > 0;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-on-surface">Attendance Rules</h2>
        <p className="mt-0.5 text-sm text-on-surface-variant">
          These rules control how punch records are interpreted. Existing punch data is never modified.
        </p>
      </div>

      {/* Office Hours */}
      <section className="rounded-lg border border-outline-variant p-4 space-y-4">
        <h3 className="text-sm font-semibold text-on-surface">Office Hours</h3>
        <div className="grid grid-cols-2 gap-4">
          <label className="space-y-1">
            <span className="text-xs text-on-surface-variant">Work starts</span>
            <input
              type="time"
              className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
              value={values.office_start_time ?? '10:00'}
              onChange={e => set('office_start_time', e.target.value)}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-on-surface-variant">Work ends</span>
            <input
              type="time"
              className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
              value={values.office_end_time ?? '19:00'}
              onChange={e => set('office_end_time', e.target.value)}
            />
          </label>
        </div>
      </section>

      {/* Check-in Rules */}
      <section className="rounded-lg border border-outline-variant p-4 space-y-4">
        <h3 className="text-sm font-semibold text-on-surface">Check-in Rules</h3>
        <div className="grid grid-cols-2 gap-4">
          <label className="space-y-1">
            <span className="text-xs text-on-surface-variant">
              Grace period (min) — arrives by <strong>{values.office_start_time}</strong> + this = on time
            </span>
            <input
              type="number" min={0} max={120}
              className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
              value={values.grace_in_minutes ?? 30}
              onChange={e => set('grace_in_minutes', Number(e.target.value))}
            />
            <p className="text-xs text-on-surface-variant">
              Latest on-time: {(() => {
                const [h, m] = (values.office_start_time ?? '10:00').split(':').map(Number);
                const total = h * 60 + m + (values.grace_in_minutes ?? 30);
                return `${String(Math.floor(total / 60)).padStart(2,'0')}:${String(total % 60).padStart(2,'0')}`;
              })()}
            </p>
          </label>
          <label className="space-y-1">
            <span className="text-xs text-on-surface-variant">
              Half-day after (min past start) — same as grace means arrivals after grace = half day
            </span>
            <input
              type="number" min={0} max={480}
              className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
              value={values.half_day_after_minutes ?? 30}
              onChange={e => set('half_day_after_minutes', Number(e.target.value))}
            />
          </label>
          <label className="space-y-1 col-span-2">
            <span className="text-xs text-on-surface-variant">
              Double-tap guard (min) — a second punch within this many minutes of check-in is ignored as a duplicate
            </span>
            <input
              type="number" min={5} max={60}
              className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
              value={values.checkout_guard_minutes ?? 30}
              onChange={e => set('checkout_guard_minutes', Number(e.target.value))}
            />
          </label>
        </div>
      </section>

      {/* Break */}
      <section className="rounded-lg border border-outline-variant p-4 space-y-4">
        <h3 className="text-sm font-semibold text-on-surface">Break / Hours</h3>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-2 col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={values.break_auto_deduct ?? true}
              onChange={e => set('break_auto_deduct', e.target.checked)}
            />
            <span className="text-sm text-on-surface">Auto-deduct lunch break from worked hours</span>
          </label>
          {values.break_auto_deduct && (
            <label className="space-y-1">
              <span className="text-xs text-on-surface-variant">Break duration (min)</span>
              <input
                type="number" min={0} max={120}
                className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
                value={values.break_minutes ?? 60}
                onChange={e => set('break_minutes', Number(e.target.value))}
              />
            </label>
          )}
          <label className="space-y-1">
            <span className="text-xs text-on-surface-variant">Full day minimum (hrs)</span>
            <input
              type="number" min={1} max={12}
              className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
              value={values.min_hours_full_day ?? 8}
              onChange={e => set('min_hours_full_day', Number(e.target.value))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-on-surface-variant">Half day minimum (hrs)</span>
            <input
              type="number" min={1} max={6}
              className="w-full rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
              value={values.min_hours_half_day ?? 4}
              onChange={e => set('min_hours_half_day', Number(e.target.value))}
            />
          </label>
        </div>
      </section>

      {/* Device auto-approve */}
      <section className="rounded-lg border border-outline-variant p-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={values.auto_approve_devices ?? false}
            onChange={e => set('auto_approve_devices', e.target.checked)}
          />
          <div>
            <span className="text-sm text-on-surface">Auto-approve new devices</span>
            <p className="text-xs text-on-surface-variant">
              When off (recommended), each new device must be approved manually in the Devices tab.
            </p>
          </div>
        </label>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-on-primary disabled:opacity-40 hover:opacity-90 transition-opacity"
        >
          {save.isPending ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}

/** Admin console for the push-based (ADMS) biometric platform. */
export function BiometricAdminPage() {
  const [tab, setTab] = useState<TabId>('devices');
  useBiometricLiveUpdates();

  return (
    <div className="space-y-6 pb-16">
      <PageHeader icon={Fingerprint} eyebrow="People" title="Biometric Administration" description={<>Devices push punches and enrolments straight to this server over the internet — no office PC and
          no port forwarding. Everything eSSL&apos;s Windows software did lives here.</>} />

      <nav className="flex flex-wrap gap-1 border-b border-outline-variant" role="tablist">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === id
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </nav>

      <div role="tabpanel">
        {tab === 'devices' && <DeviceList />}
        {tab === 'employees' && (
          <div className="space-y-4">
            <UnmappedPinsPanel />
            <EmployeeEnrollTable />
          </div>
        )}
        {tab === 'punches' && <PunchLogTable />}
        {tab === 'regularisations' && <RegularisationPanel />}
        {tab === 'reports' && <BiometricReports />}
        {tab === 'commands' && <CommandQueuePanel />}
        {tab === 'logs' && <DeviceOpLogPanel />}
        {tab === 'settings' && <BiometricFullSettingsPanel />}
      </div>
    </div>
  );
}

export default BiometricAdminPage;
